import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { 
  Sparkles, Trophy, Flame, ExternalLink, CheckCircle, 
  Target, Calendar, Zap
} from 'lucide-react';

const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WellnessSection({ maskedId }) {
  const { user, token } = useAuth();
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({ current_streak: 0, total_checkins: 0 });
  const [loading, setLoading] = useState(true);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [todayCheckedIn, setTodayCheckedIn] = useState(false);

  useEffect(() => {
    fetchData();
  }, [maskedId]);

  const fetchData = async () => {
    try {
      // Fetch wellness partners
      const partnersRes = await fetch(`${API_URL}/api/mvp/wellness/partners?active_only=true`);
      const partnersData = await partnersRes.json();
      setPartners(partnersData || []);
      
      // Fetch wellness stats
      if (maskedId) {
        const statsRes = await fetch(`${API_URL}/api/mvp/wellness/stats/${maskedId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const statsData = await statsRes.json();
        setStats(statsData);
        
        // Check if already checked in today
        if (statsData.last_checkin_date) {
          const lastDate = new Date(statsData.last_checkin_date).toDateString();
          const today = new Date().toDateString();
          setTodayCheckedIn(lastDate === today);
        }
      }
    } catch (err) {
      console.error('Failed to fetch wellness data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDailyCheckin = async () => {
    if (!maskedId || todayCheckedIn) return;
    setCheckinLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/mvp/wellness/checkin?user_id=${user.id}&masked_id=${maskedId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ activity_type: 'daily_checkin' })
      });
      const data = await res.json();
      if (data.stats) {
        setStats(data.stats);
        setTodayCheckedIn(true);
      }
    } catch (err) {
      console.error('Failed to log checkin:', err);
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleStartChallenge = (partner) => {
    // Log the challenge start
    fetch(`${API_URL}/api/mvp/wellness/checkin?user_id=${user?.id}&masked_id=${maskedId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        activity_type: 'challenge_started',
        partner_id: partner.id
      })
    }).catch(console.error);
    
    // Open partner URL
    window.open(partner.url, '_blank');
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-purple-200 rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-purple-100 rounded mb-4"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-xl">
          <Sparkles className="text-purple-600" size={24} />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Wellness Check-ins</h2>
          <p className="text-sm text-gray-600">Daily self-reported check-ins</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="text-orange-500" size={20} />
            <span className="text-xs text-gray-500 uppercase">Streak</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{stats.current_streak}</p>
          <p className="text-xs text-gray-500">days</p>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="text-yellow-500" size={20} />
            <span className="text-xs text-gray-500 uppercase">Total</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{stats.total_checkins}</p>
          <p className="text-xs text-gray-500">check-ins</p>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm col-span-2 sm:col-span-1">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="text-blue-500" size={20} />
            <span className="text-xs text-gray-500 uppercase">Today</span>
          </div>
          {todayCheckedIn ? (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle size={20} />
              <span className="font-medium">Done!</span>
            </div>
          ) : (
            <button
              onClick={handleDailyCheckin}
              disabled={checkinLoading}
              className="w-full py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm rounded-lg hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {checkinLoading ? 'Logging...' : (
                <>
                  <Zap size={16} />
                  Check In
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Streak Message */}
      {stats.current_streak > 0 && (
        <div className="bg-gradient-to-r from-orange-100 to-yellow-100 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Flame className="text-orange-500" size={28} />
          <div>
            <p className="font-semibold text-gray-800">
              {stats.current_streak}-day check-in streak
            </p>
            <p className="text-sm text-gray-600">Check in daily.</p>
          </div>
        </div>
      )}

      {/* Wellness Partners */}
      {partners.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Target size={16} />
            Wellness Challenges
          </h3>
          <div className="space-y-3">
            {partners.map(partner => (
              <div 
                key={partner.id}
                className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-4"
              >
                {partner.logo_url ? (
                  <img 
                    src={partner.logo_url} 
                    alt={partner.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
                    <Sparkles className="text-white" size={24} />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">{partner.name}</h4>
                  <p className="text-sm text-gray-500 line-clamp-1">{partner.description}</p>
                </div>
                <button
                  onClick={() => handleStartChallenge(partner)}
                  className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center gap-2 text-sm font-medium"
                >
                  Start
                  <ExternalLink size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-gray-400 mt-4 text-center">
        Self-reported check-ins. Not a substitute for clinical care.
      </p>
    </div>
  );
}
